import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { CustomInput, Col, Row, Button, Input, Label } from "reactstrap";
import ColorSelectField from "../../../components/ColorSelectField";
import shopProductSelectors from "../../../redux/shop/products/selectors";
import { COLORTYPES, COLORS } from "../../../utils/colors";

const ProductCustomization = (
  {
    handleMaterialChange,
    handleColorChange,
    addToCartProcessing,
    selectedColor,
    productId,
    handleCustomSizeChange,
    materialSerie,
    custom_size,
  },
  context
) => {
  // Fetch the language from the Redux store
  const language = useSelector((state) => state.i18nState.lang);

  const shopProduct = useSelector((state) =>
    shopProductSelectors.selectProductById(state, language, productId)
  );

  const shopProductData = {
    attributes: shopProduct?.data?.attributes,
    relationships: shopProduct?.data?.relationships,
    included: shopProduct?.included,
  };

  if (!shopProductData.included || !shopProductData.relationships?.option_types)
    return null;

  const renderCustomColorSelect = (optionData) => (
    <>
      <Col xs={6}>
        <CustomInput
          type="select"
          id="materialSerie"
          onChange={handleMaterialChange}
          value={parseInt(materialSerie)}
          required
          disabled={addToCartProcessing}
          className="data-hj-allow"
        >
          {Object.keys(COLORTYPES).map((prod) => (
            <option value={parseInt(COLORTYPES[prod])} key={`product_${prod}`}>
              {context.t(`${prod}`)}
              {` (${COLORS[COLORTYPES[prod]].length} ${context.t("Colors")})`}
            </option>
          ))}
        </CustomInput>
      </Col>
      <Col xs={6}>
        <ColorSelectField
          input={{
            onChange: handleColorChange,
            value: selectedColor,
          }}
          disabled={addToCartProcessing}
          name="color"
          materialSerie={materialSerie}
        />
      </Col>
    </>
  );

  const renderCustomSizeInput = (minSize, maxSize) => (
    <>
      <Col xs={6}>
        <Label for="custom_width">{context.t("width in cm")}</Label>
        <Input
          name="custom_width"
          value={custom_size.custom_width || minSize?.attributes?.width || null}
          onChange={handleCustomSizeChange}
          min={minSize?.attributes?.width || 1}
          max={maxSize?.attributes?.width || 120}
          placeholder={context.t("e.g. 25cm")}
          style={{ textAlign: "center" }}
          className="mr-1 no-spin-buttons data-hj-allow"
          type="number"
          step="1"
        />
      </Col>
      <Col xs={6}>
        <Label for="custom_height">{context.t("height in cm")}</Label>
        <Input
          name="custom_height"
          value={
            custom_size.custom_height || minSize?.attributes?.height || null
          }
          onChange={handleCustomSizeChange}
          min={minSize?.attributes?.height || 1}
          max={maxSize?.attributes?.height || 120}
          placeholder={context.t("e.g. 25cm")}
          style={{ textAlign: "center" }}
          className="mr-1 no-spin-buttons data-hj-allow"
          type="number"
          step="1"
        />
      </Col>
      <Col xs={12} className="pt-2">
        {renderSizeVariants()}
      </Col>
    </>
  );

  const renderSizeVariants = () => {
    const variants = shopProductData.included?.filter(
      (obj) => obj.type === "variant"
    );
    if (!variants) return null;

    return variants
      .filter(
        (thing, index, self) =>
          index ===
          self.findIndex(
            (t) =>
              t.attributes.width === thing.attributes.width &&
              t.attributes.height === thing.attributes.height
          )
      )
      .sort((a, b) => a.attributes.width - b.attributes.width)
      .map((variant, idx) => (
        <Button
          key={`variant-${idx}`}
          color="primary"
          onClick={() =>
            handleCustomSizeChange({
              custom_width: variant.attributes.width,
              custom_height: variant.attributes.height,
            })
          }
          size="sm"
          outline
          className="mr-1 mb-1"
        >
          {variant.attributes.width}cm x {variant.attributes.height}cm
        </Button>
      ));
  };

  const renderOptions = () => {
    return shopProductData.relationships.option_types.data.map((p_option) => {
      const optionData = shopProductData.included.find(
        (obj) =>
          obj.id === p_option.id &&
          obj.type === "option_type" &&
          (obj.attributes.name === "custom_size" ||
            obj.attributes.name === "custom_colorselect")
      );
      if (!optionData) return null;

      const minSize =
        optionData.attributes.name === "custom_size"
          ? shopProductData.included.find(
              (obj) =>
                obj.type === "variant" &&
                obj.attributes.options_text.includes("min")
            )
          : false;

      const maxSize =
        optionData.attributes.name === "custom_size"
          ? shopProductData.included.find(
              (obj) =>
                obj.type === "variant" &&
                obj.attributes.options_text.includes("max")
            )
          : false;

      return (
        <Row key={optionData.id} className="mb-2">
          <span className="h6 col-12">
            {optionData.attributes.presentation}
          </span>
          {optionData.attributes.name === "custom_colorselect" &&
            renderCustomColorSelect(optionData)}
          {optionData.attributes.name === "custom_size" &&
            renderCustomSizeInput(minSize, maxSize)}
        </Row>
      );
    });
  };

  return <>{renderOptions()}</>;
};

ProductCustomization.propTypes = {
  handleMaterialChange: PropTypes.func.isRequired,
  handleColorChange: PropTypes.func.isRequired,
  addToCartProcessing: PropTypes.bool.isRequired,
  selectedColor: PropTypes.string.isRequired,
  productId: PropTypes.string.isRequired,
  handleCustomSizeChange: PropTypes.func.isRequired,
  materialSerie: PropTypes.string.isRequired,
  custom_size: PropTypes.object.isRequired,
};

/** Define proptype for the 'translation' function */
ProductCustomization.contextTypes = {
  t: PropTypes.func,
};

export default ProductCustomization;
